import { cartChange } from '@process-creative/slate-theme-tools';

// Set up the inner template
const tmpl = document.createElement('template');
tmpl.innerHTML = `
  <slot></slot>
`;

export const EVT_QTY_CHANGED = 'onQtyChange';
export class EventQtyChanged extends CustomEvent<{
  qtySelector:QuantitySelectorElement,
  newValue:number
}> {
}

export class QuantitySelectorElement extends HTMLElement {
  // Getters/Setters
  get value():number {
    return this.input ? this.input.valueAsNumber : this.fallbackValue;
  }

  set value(value:number) {
    if(this.max !== null) value = Math.min(this.max, value);
    if(this.min !== null) value = Math.max(this.min, value);
    if(this.input) this.input.valueAsNumber = value;
    this.fallbackValue = value;
  }

  get min():number|null {
    const strMin = this.input ? this.input.min : this.fallbackMin;
    if(strMin === null) return null;
    if(typeof strMin === typeof '') {
      const iMin = parseInt(strMin as string);
      if(!(strMin as string).replace(/\s/g, '').length) return null;
      if(isNaN(iMin)) return null;
    }
    return strMin as number;
  }

  set min(value:number|null) {
    if(value === null) {
      if(this.input) this.input.min = '';
      this.fallbackMin = null;
    } else {
      if(this.input) this.input.min = value.toString();
      this.fallbackMin = this.fallbackMin;
    }
  }

  get max():number|null {
    const strMax = this.input ? this.input.max : this.fallbackMax;
    if(strMax === null) return null;
    if(typeof strMax === typeof '') {
      const iMax = parseInt(strMax as string);
      if(!(strMax as string).replace(/\s/g, '').length) return null;
      if(isNaN(iMax)) return null;
    }
    return strMax as number;
  }

  set max(value:number|null) {
    if(value === null) {
      if(this.input) this.input.max = '';
      this.fallbackMax = null;
    } else {
      if(this.input) this.input.max = value.toString();
      this.fallbackMax = this.fallbackMin;
    }
  }

  get disabled():boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(disabled:boolean) {
    if(disabled) {
      this.setAttribute('disabled', 'disabled');
    } else {
      this.removeAttribute('disabled');
    }
    this.updateDisabled();
  }

  // Properties
  private readonly input:HTMLInputElement|null = null;
  private readonly btnMinus:HTMLButtonElement|null = null;
  private readonly btnPlus:HTMLButtonElement|null = null;
  private readonly line:string|null = null;

  private fallbackValue:number = 0;
  private fallbackMin:number|null = null;
  private fallbackMax:number|null = null;

  constructor() {
    super();

    // Set up the shadow
    const sr = this.attachShadow({ mode: 'open' });
    sr.appendChild(tmpl.content.cloneNode(true));

    // Get the children
    this.btnMinus = this.querySelector('.is-minus');
    this.btnPlus = this.querySelector('.is-plus');
    this.input = this.querySelector('input[type="number"]');

    // Set up class lists
    this.classList.add('o-quantity-selector');

    this.line = this.getAttribute('line');

    // Add event listeners
    this.btnMinus?.addEventListener('click', this.onMinusClick);
    this.btnPlus?.addEventListener('click', this.onPlusClick);
    this.input?.addEventListener('change', this.onInputChanged);

    this.updateDisabled();
  }

  private onPlusClick = (e:MouseEvent) => {
    e.preventDefault();
    this.value += 1;
    this.onQuantityChanged();
  }

  private onMinusClick = (e:MouseEvent) => {
    e.preventDefault();
    this.value -= 1;
    this.onQuantityChanged();
  }

  private onInputChanged = (e:Event) => {
    this.value = this.input?.valueAsNumber || this.value;
    this.onQuantityChanged();
  }

  private onQuantityChanged() {
    if (this.line) {
      try {
        cartChange({ quantity: this.value, id:this.line });
      } catch(e) {
        console.warn(e);
      }
    }

    const evt = new EventQtyChanged(EVT_QTY_CHANGED, {
      bubbles: true,
      cancelable: false,
      detail: {
        qtySelector:this, newValue: this.value
      }
    });

    this.dispatchEvent(evt);
  }

  private updateDisabled() {
    const de = (n?:(HTMLElement & { disabled:boolean })|null) => {
      if(!n) return;
      n.disabled = true;
      n.classList.add('is-disabled');
    }

    const ee = (n?:(HTMLElement & { disabled:boolean })|null) => {
      if(!n) return;
      n.disabled = false;
      n.classList.remove('is-disabled');
    }

    // Disable / Enable the children
    [
      this.btnMinus, this.btnPlus, this.input
    ].forEach(e => {
      this.disabled ? de(e) : ee(e);
    })
  
    if(this.disabled) {
      this.classList.add('is-disabled');
    } else {
      this.classList.remove('is-disabled');
    }
  }
}

export const generateQuantitySelector = (
  qty:number = 0,
  min:number = 0,
  max?:number,
  clazz?:string,
  attr?:string
) => {
  return /*html*/`
    <quantity-selector class="${clazz}" ${attr}>
      <button type="button" class="o-quantity-selector__btn is-minus">
        -
      </button>
      <input
        type="number"
        class="o-quantity-selector__input"
        name="quantity"
        value="${qty}"
        min="${min}"
        ${max ? `max="${max}"` : ``}
      />
      <button type="button" class="o-quantity-selector__btn is-plus">
        +
      </button>
    </quantity-selector>
  `;
}

if(!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelectorElement);
}